import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

import * as knowledgeAPI from '@/api/knowledge/api';
import { mapKnowledgeTagDTO, mapKnowledgeTagListDTO } from '@/models/knowledge-tag.mapper';
import { KnowledgeTag } from '@/models/knowledge-tag.model';
import { createLogger } from '@/utils/logging';

const logger = createLogger('KnowledgeTagStore', 'hook');

/* ------------------------------------------------------------------ */
/*  State                                                              */
/* ------------------------------------------------------------------ */

export interface KnowledgeTagState {
  /** All available tags for the current client/project scope. */
  tags: KnowledgeTag[];
  loading: boolean;
  error: string | null;
}

const initialState: KnowledgeTagState = {
  tags: [],
  loading: false,
  error: null,
};

/* ------------------------------------------------------------------ */
/*  Actions                                                            */
/* ------------------------------------------------------------------ */

export enum KnowledgeTagActionType {
  FETCH_START = 'FETCH_START',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_ERROR = 'FETCH_ERROR',
  ADD_TAG = 'ADD_TAG',
  CLEAR_ERROR = 'CLEAR_ERROR',
}

export interface KnowledgeTagAction {
  type: KnowledgeTagActionType;
  payload?: unknown;
}

/* ------------------------------------------------------------------ */
/*  Reducer                                                            */
/* ------------------------------------------------------------------ */

const knowledgeTagReducer = (
  state: KnowledgeTagState,
  action: KnowledgeTagAction,
): KnowledgeTagState => {
  switch (action.type) {
    case KnowledgeTagActionType.FETCH_START:
      return { ...state, loading: true, error: null };

    case KnowledgeTagActionType.FETCH_SUCCESS:
      return {
        ...state,
        tags: (action.payload as KnowledgeTag[]) ?? [],
        loading: false,
        error: null,
      };

    case KnowledgeTagActionType.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: (action.payload as string) ?? 'An error occurred',
      };

    case KnowledgeTagActionType.ADD_TAG: {
      const newTag = action.payload as KnowledgeTag;
      const exists = state.tags.some((t) => t.tagId === newTag.tagId);
      if (exists) {
        return state;
      }
      return { ...state, tags: [...state.tags, newTag] };
    }

    case KnowledgeTagActionType.CLEAR_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};

/* ------------------------------------------------------------------ */
/*  Context type                                                       */
/* ------------------------------------------------------------------ */

interface KnowledgeTagContextType extends KnowledgeTagState {
  /** Fetch all tags for the given client (optionally scoped to project). */
  fetchTags: (accessToken: string, clientId: string, projectId?: string | null) => Promise<void>;
  /** Create a new tag and add it to the local store. Returns the new domain model. */
  createTag: (
    accessToken: string,
    clientId: string,
    tagName: string,
    projectId?: string | null,
  ) => Promise<KnowledgeTag>;
  /** Assign tag names to a persisted card. Returns updated card tags array. */
  assignTagsToCard: (
    accessToken: string,
    clientId: string,
    cardId: string,
    tagNames: string[],
  ) => Promise<string[]>;
  /** Remove a single tag from a persisted card. Returns updated card tags array. */
  removeTagFromCard: (
    accessToken: string,
    clientId: string,
    cardId: string,
    tagId: string,
  ) => Promise<string[]>;
  /** Find a tag model by its name (case-insensitive). */
  findTagByName: (tagName: string) => KnowledgeTag | undefined;
  clearError: () => void;
}

const KnowledgeTagContext = createContext<KnowledgeTagContextType | undefined>(undefined);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

/** Props for the KnowledgeTagProvider component. */
export interface KnowledgeTagProviderProps {
  children: ReactNode;
}

/**
 * Provides tag list state plus create, assign, and remove actions to the React tree.
 * The tag list is populated by calling `fetchTags`; mutations update the list in-place where
 * possible without requiring a full refetch.
 */
export const KnowledgeTagProvider: React.FC<KnowledgeTagProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(knowledgeTagReducer, initialState);

  const fetchTags = useCallback(
    async (accessToken: string, clientId: string, projectId?: string | null) => {
      dispatch({ type: KnowledgeTagActionType.FETCH_START });
      try {
        const response = await knowledgeAPI.listKnowledgeTags(accessToken, clientId, projectId);
        const tags = mapKnowledgeTagListDTO(response.items, clientId);
        dispatch({ type: KnowledgeTagActionType.FETCH_SUCCESS, payload: tags });
        logger.info('Knowledge tags fetched', { count: tags.length });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tags';
        dispatch({ type: KnowledgeTagActionType.FETCH_ERROR, payload: errorMessage });
        logger.error('Failed to fetch knowledge tags', error);
      }
    },
    [],
  );

  const createTag = useCallback(
    async (
      accessToken: string,
      clientId: string,
      tagName: string,
      projectId?: string | null,
    ): Promise<KnowledgeTag> => {
      try {
        const dto = await knowledgeAPI.createKnowledgeTag(accessToken, clientId, {
          project_id: projectId ?? null,
          tag_name: tagName,
        });
        const tag = mapKnowledgeTagDTO(dto, clientId);
        dispatch({ type: KnowledgeTagActionType.ADD_TAG, payload: tag });
        logger.info('Knowledge tag created', { tagId: tag.tagId, tagName: tag.tagName });
        return tag;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create tag';
        logger.error('Failed to create knowledge tag', error);
        throw new Error(errorMessage);
      }
    },
    [],
  );

  const assignTagsToCard = useCallback(
    async (
      accessToken: string,
      clientId: string,
      cardId: string,
      tagNames: string[],
    ): Promise<string[]> => {
      try {
        const response = await knowledgeAPI.assignKnowledgeCardTags(accessToken, clientId, cardId, {
          tags: tagNames,
        });
        logger.info('Tags assigned to card', { cardId, tags: tagNames });
        return response.tags;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to assign tags';
        logger.error('Failed to assign tags to card', error);
        throw new Error(errorMessage);
      }
    },
    [],
  );

  const removeTagFromCard = useCallback(
    async (
      accessToken: string,
      clientId: string,
      cardId: string,
      tagId: string,
    ): Promise<string[]> => {
      try {
        const response = await knowledgeAPI.removeKnowledgeCardTag(
          accessToken,
          clientId,
          cardId,
          tagId,
        );
        logger.info('Tag removed from card', { cardId, tagId });
        return response.tags;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to remove tag';
        logger.error('Failed to remove tag from card', error);
        throw new Error(errorMessage);
      }
    },
    [],
  );

  const findTagByName = useCallback(
    (tagName: string): KnowledgeTag | undefined => {
      const normalized = tagName.trim().toLowerCase();
      return state.tags.find((t) => t.tagName.toLowerCase() === normalized);
    },
    [state.tags],
  );

  const clearError = useCallback(() => {
    dispatch({ type: KnowledgeTagActionType.CLEAR_ERROR });
  }, []);

  const value = useMemo(
    (): KnowledgeTagContextType => ({
      ...state,
      fetchTags,
      createTag,
      assignTagsToCard,
      removeTagFromCard,
      findTagByName,
      clearError,
    }),
    [state, fetchTags, createTag, assignTagsToCard, removeTagFromCard, findTagByName, clearError],
  );

  return <KnowledgeTagContext.Provider value={value}>{children}</KnowledgeTagContext.Provider>;
};

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Accesses the knowledge tag store from the nearest KnowledgeTagProvider.
 * Throws if called outside a KnowledgeTagProvider.
 *
 * @returns Tag list, loading/error state, and CRUD actions.
 */
export const useKnowledgeTagContext = (): KnowledgeTagContextType => {
  const context = useContext(KnowledgeTagContext);
  if (!context) {
    throw new Error('useKnowledgeTagContext must be used within KnowledgeTagProvider');
  }
  return context;
};
